import { useEffect, type ReactNode } from "react";
import { Icono } from "./Icono";

const FOCO = "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secundario focus-visible:ring-offset-2";

// `onCerrar` es el único punto de cierre: Escape, clic fuera del panel y el
// botón "X" llaman exactamente al mismo callback. Un formulario con cambios
// sin guardar simplemente envuelve ese `onCerrar` con
// `useCambiosSinGuardar().intentarSalir(...)` — el Modal no necesita saber
// nada sobre "dirty", sólo garantizar que Escape/clic-afuera pasan por el
// mismo camino que el botón explícito de cerrar.
export function Modal({
  titulo,
  abierto,
  onCerrar,
  children,
  ancho = "max-w-lg",
}: {
  titulo: string;
  abierto: boolean;
  onCerrar: () => void;
  children: ReactNode;
  ancho?: string;
}) {
  // Devuelve el foco al elemento que abrió el modal — sólo se recalcula en
  // la transición de apertura (no en cada render con el modal ya abierto,
  // para no terminar "recapturando" el foco de un campo interno).
  useEffect(() => {
    if (!abierto) return;
    const elementoPrevio = document.activeElement as HTMLElement | null;
    return () => elementoPrevio?.focus();
  }, [abierto]);

  useEffect(() => {
    if (!abierto) return;
    function alPresionarTecla(e: KeyboardEvent) {
      if (e.key === "Escape") onCerrar();
    }
    window.addEventListener("keydown", alPresionarTecla);
    return () => window.removeEventListener("keydown", alPresionarTecla);
  }, [abierto, onCerrar]);

  if (!abierto) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-principal/40 p-4 pt-16"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCerrar();
      }}
    >
      <div className={`w-full ${ancho} rounded-xl bg-superficie shadow-xl`}>
        <div className="flex items-center justify-between border-b border-borde px-5 py-4">
          <h2 className="text-base font-semibold text-texto">{titulo}</h2>
          <button
            onClick={onCerrar}
            aria-label="Cerrar"
            className={`rounded-md p-1 text-texto-secundario transition-colors hover:bg-fondo hover:text-texto ${FOCO}`}
          >
            <Icono nombre="cerrar" className="h-4 w-4" />
          </button>
        </div>
        <div className="max-h-[70vh] overflow-y-auto p-5">{children}</div>
      </div>
    </div>
  );
}
